/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { forwardRef } from "react";

import cn from "classnames";

import useDevAttributes from "hooks/useDevAttributes";

import { DuplicateIcon, EditIcon, NextIcon } from "components/icons";
import { Button } from "components/ui/Button";
import Text from "components/ui/Text";

interface IMenuItem {
  title: string | React.ReactNode;
  icon?: string | React.ReactNode;
  onDuplicate?: () => void;
  onEdit?: () => void;
  onNext: () => void;
  className?: string;
  index?: number;
}

const MenuItem = forwardRef<HTMLLIElement, IMenuItem>((props, ref) => {
  const { title, icon, onDuplicate, onEdit, onNext, className, index } = props;

  // @ts-ignore
  const { duplicateAttr, editAttr } = useDevAttributes({
    duplicateAttr: `duplicate-${title}`,
    editAttr: `edit-${title}`,
  });

  return (
    <li ref={ref} test-id="v-PZP1m3">
      <div
        role="button"
        onClick={onNext}
        className={cn(
          "flex px-4 h-16 items-center justify-between bg-tertiary hover:bg-tertiary-accent transition-colors text-white text-lg",
          className,
        )}
        test-id="4UmpHUcW"
      >
        <div className="flex items-center space-x-1.5" test-id="J951tShX">
          {icon && <span test-id="fgxwoHfm">{icon}</span>}
          <div className="w-36 truncate" test-id="cE7depHU">
            <Text as="strong" className="text-base" test-id="tlAAhhWN">
              {title}
            </Text>
          </div>
        </div>

        <div className="flex space-x-1.5" test-id="R2Xh1J--">
          {onDuplicate && (
            <Button
              {...duplicateAttr}
              variant="ghost"
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.stopPropagation();
                onDuplicate();
              }}
              size="tiny"
              shape="circle"
              test-id="A8AakdUV"
            >
              <span className="pr-0.5" test-id="hLli3hJG">
                <DuplicateIcon className="-ml-px" test-id="aYkROCv6" />
              </span>
            </Button>
          )}

          {onEdit && (
            <Button
              {...editAttr}
              variant="ghost"
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.stopPropagation();
                onEdit();
              }}
              size="tiny"
              shape="circle"
              test-id="YEe840Do"
            >
              <span className="" test-id="HYAI1AgW">
                <EditIcon testId={`menu-item-edit-${index}`} />
              </span>
            </Button>
          )}

          <Button
            shape="circle"
            variant="ghost"
            size="tiny"
            onClick={onNext}
            test-id="QYN9IKRq"
          >
            <span className="py-0.5" test-id="I7tOxv2B">
              <NextIcon className="-mr-px" test-id="k2kXGhYZ" />
            </span>
          </Button>
        </div>
      </div>
    </li>
  );
});

MenuItem.displayName = "MenuItem";

export default MenuItem;
